import React from 'react';

import { EstimatedTrafficSelect } from './EstimatedTrafficSelect/EstimatedTrafficSelect';
import type { IButtonPosition, InquiryValues } from '@components/Form/Form';
import { Form } from '@components/Form/Form';
import { Input } from '@components/Input/Input';
import { TextArea } from '@components/Input/TextArea';
import {
  email,
  emailEmpty,
  messageEmpty,
  nameEmpty,
  websiteEmpty,
} from '@containers/Validator/validator';
import type { InquiryTarget } from '@hooks/api/useSendInquiryMutation';
import { useFocus } from '@hooks/useFocus';

export interface IContactForm {
  id?: string;
  target: InquiryTarget;
  submitButtonText?: string;
  submitButtonPosition?: IButtonPosition;
  subject: string;
  textarea?: {
    label?: string;
    placeholder?: string;
    tooltip?: string;
    required?: boolean;
  };
  focusSectionId?: string;
  buttonWidth?: 'full' | 'auto';
  errorPosition?: 'top' | 'right';
  customThankYouComponent?: React.ComponentProps<typeof Form>['customThankYouComponent'];
  onSuccess?: (values: InquiryValues) => void;
}

interface IProps extends IContactForm {
  websiteInput?: boolean;
  estimatedTrafficInput?: boolean;
}

export const ContactForm: React.FC<IProps> = ({
  id,
  target,
  subject,
  submitButtonText = 'Get in touch',
  submitButtonPosition,
  textarea,
  focusSectionId,
  errorPosition,
  buttonWidth = 'auto',
  customThankYouComponent,
  websiteInput = false,
  estimatedTrafficInput = false,
  onSuccess,
}) => {
  const focus = useFocus(focusSectionId);

  return (
    <Form
      buttonPosition={submitButtonPosition}
      buttonText={submitButtonText}
      buttonWidth={buttonWidth}
      customThankYouComponent={customThankYouComponent}
      errorPosition={errorPosition}
      id={id}
      initialValues={{
        name: '',
        email: '',
        message: '',
        website: '',
        estimatedTraffic: '',
      }}
      subject={subject}
      target={target}
      onSuccess={onSuccess}
    >
      <Input
        autofocus={focus}
        id="contact-name"
        label="Full name"
        name="name"
        placeholder="John Smith"
        type="text"
        validate={nameEmpty}
      />
      <Input
        id="contact-email"
        label="Business email"
        name="email"
        placeholder="j.smith@cdn77.com"
        type="email"
        validate={value => emailEmpty(value) ?? email(value)}
      />
      {websiteInput && (
        <Input
          id="contact-company"
          label="Company website / Project name"
          name="website"
          placeholder="www.example.com"
          type="text"
          validate={websiteEmpty}
        />
      )}
      {estimatedTrafficInput && <EstimatedTrafficSelect />}
      <TextArea
        id="contact-message"
        label={textarea?.label ?? 'Use case description'}
        name="message"
        placeholder={textarea?.placeholder ?? 'Leave your message here...'}
        rows={7}
        tooltipText={textarea?.tooltip}
        validate={textarea?.required === false ? undefined : messageEmpty}
      />
    </Form>
  );
};
